import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Photos } from "../lib/api";
import { LocalStore, buildFilename, padSeq } from "../lib/storage";
import { CaptureMoment } from "../lib/captureMoment";
import { queueShot, onPendingChange, pendingUploads as pendingCount } from "../lib/captureQueue";
import { loadSettings, saveSettings, cleanSku } from "../lib/captureSettings";
import { createKeyRouter } from "../lib/keyScanner";
import { useBarcodeScan } from "../lib/useBarcodeScan";
import { CaptureModesBar } from "../components/CaptureModesBar";
import { CaptureSettingsSheet } from "../components/CaptureSettingsSheet";

const shotTags = (base, sku) => (sku ? [sku, base].filter(Boolean).join(",") : base);
import {
  X,
  ArrowsClockwise,
  Image as ImageIcon,
  Lightning,
  LightningSlash,
  Circle,
  CheckCircle,
  Microphone,
  MicrophoneSlash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { RecorderSupported, recordAndTranscribe, parseTagList } from "../lib/voice";

const vibrate = (pattern) => {
  try {
    if (navigator.vibrate) navigator.vibrate(pattern);
  } catch { /* noop */ }
};

export default function CameraPage() {
  const nav = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const [active, setActive] = useState(LocalStore.getActiveSession());
  const [seq, setSeq] = useState(active?.current_sequence || 1);
  const [thumbs, setThumbs] = useState([]); // recent shots, in-memory
  const [previewThumb, setPreviewThumb] = useState(null); // tap to preview
  const [facing, setFacing] = useState("environment");
  const [flash, setFlash] = useState(false); // visual confirmation overlay
  const [torch, setTorch] = useState(false);
  const [bursting, setBursting] = useState(false);
  const [settings, setSettingsState] = useState(loadSettings);
  const [showSettings, setShowSettings] = useState(false);
  const [sku, setSkuState] = useState(null);
  const [scanOn, setScanOn] = useState(false);
  const [tl, setTl] = useState({ running: false, taken: 0 });
  const settingsRef = useRef(settings);
  const skuRef = useRef(null);
  const tlTimer = useRef(null);
  const setSettings = (s) => { settingsRef.current = s; setSettingsState(s); saveSettings(s); };
  const setSku = (v) => { skuRef.current = v; setSkuState(v); };
  const [pendingUploads, setPendingUploads] = useState(0);
  const seqRef = useRef(seq);
  const activeRef = useRef(active);
  const holdTimer = useRef(null);
  const burstTimer = useRef(null);
  const captureRef = useRef(() => {});
  const burstRef = useRef(() => {});
  const onScanRef = useRef(() => {});
  useEffect(() => { seqRef.current = seq; }, [seq]);
  useEffect(() => { activeRef.current = active; }, [active]);
  const [errorMsg, setErrorMsg] = useState(null);
  const [errorKind, setErrorKind] = useState(null); // https|permission|not-found|in-use|generic
  const [ready, setReady] = useState(false);
  const [shotCount, setShotCount] = useState(0);
  // iter-37: device picker state. Populated after first successful stream.
  // Empty until permission is granted (labels/deviceIds are permission-gated).
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  // iter-23: track most-recent saved photo so the voice-note mic knows
  // which photo to attach a `display_tag` update to.
  const [lastPhoto, setLastPhoto] = useState(null);
  const [voiceNoteListening, setVoiceNoteListening] = useState(false);
  const voiceStopRef = useRef(null);

  useEffect(() => {
    if (!active) {
      nav("/setup");
      return;
    }
    startCamera(facing);
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = async (mode, explicitDeviceId = null) => {
    setErrorMsg(null);
    setErrorKind(null);
    setReady(false);
    stopCamera();

    // Pre-flight: browsers require HTTPS (or localhost) for getUserMedia.
    // Detecting this up-front gives the user a fixable answer instead of
    // a cryptic NotAllowedError.
    if (
      typeof window !== "undefined" &&
      window.location &&
      window.location.protocol !== "https:" &&
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1"
    ) {
      setErrorKind("https");
      setErrorMsg(
        "Camera requires HTTPS. Open this page over https:// (or on localhost) and try again."
      );
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrorKind("unsupported");
      setErrorMsg(
        "This browser does not expose a camera API. Update to a recent Chrome, Safari, Firefox, or Edge."
      );
      return;
    }

    // Build progressive constraints. We try the most-specific first, then
    // fall back until something works. This is what makes "any camera on
    // any device" actually true instead of aspirational.
    const attempts = [];
    if (explicitDeviceId) {
      attempts.push({
        video: {
          deviceId: { exact: explicitDeviceId },
          width: { ideal: 1920 },
          height: { ideal: 1440 },
        },
        audio: false,
      });
    }
    attempts.push({
      video: {
        facingMode: { ideal: mode },
        width: { ideal: 1920 },
        height: { ideal: 1440 },
      },
      audio: false,
    });
    // Final fallback: any camera at all, any resolution. This is what
    // rescues single-webcam laptops that reject `facingMode: environment`.
    attempts.push({ video: true, audio: false });

    let stream = null;
    let lastErr = null;
    for (const constraints of attempts) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
        break;
      } catch (e) {
        lastErr = e;
      }
    }

    if (!stream) {
      const err = lastErr || {};
      const name = err.name || "";
      // Distinct error surfaces so the user knows what to fix.
      if (name === "NotAllowedError" || name === "SecurityError") {
        setErrorKind("permission");
        setErrorMsg(
          "Camera permission denied. Click the camera icon in your browser's address bar to allow access, then Retry."
        );
      } else if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError") {
        setErrorKind("not-found");
        setErrorMsg(
          "No camera found. Plug in a webcam, or on desktop check that your camera isn't disabled in system settings."
        );
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        setErrorKind("in-use");
        setErrorMsg(
          "Camera is in use by another app. Close Zoom, Meet, Teams, or any other tab using the camera, then Retry."
        );
      } else {
        setErrorKind("generic");
        setErrorMsg(
          `Could not start camera (${name || "unknown error"}). Try Retry, flip the camera, or restart your browser.`
        );
      }
      return;
    }

    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      await videoRef.current.play().catch(() => {});
      const v = videoRef.current;
      if (v && !v.videoWidth) {
        await new Promise((res) => {
          v.addEventListener("loadedmetadata", res, { once: true });
          setTimeout(res, 1500);
        });
      }
      setReady(true);
    }

    // Track which device we ended up on so the picker highlights it.
    try {
      const t = stream.getVideoTracks()[0];
      const settings = t?.getSettings?.() || {};
      if (settings.deviceId) setDeviceId(settings.deviceId);
    } catch { /* noop */ }

    // Enumerate devices AFTER first grant (labels are permission-gated).
    // A user with 3 cameras now sees them all in the picker.
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const cams = list.filter((d) => d.kind === "videoinput");
      setDevices(cams);
    } catch { /* enumeration is best-effort */ }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const flip = async () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    setTorch(false);
    setDeviceId(null); // let facingMode drive selection again
    await startCamera(next);
  };

  const pickDevice = async (id) => {
    // Direct device selection — bypasses facingMode entirely. Essential
    // for multi-webcam desktops and phones with wide/tele/macro lenses
    // exposed as distinct deviceIds.
    setDeviceId(id);
    setTorch(false);
    await startCamera(facing, id);
  };

  const toggleTorch = async () => {
    try {
      const track = streamRef.current?.getVideoTracks()?.[0];
      const caps = track?.getCapabilities?.() || {};
      if (!caps.torch) {
        toast.message("Torch not supported on this device");
        return;
      }
      const next = !torch;
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorch(next);
    } catch {
      toast.error("Could not toggle torch");
    }
  };

  // Shutter path: grab the frame (~10 ms) and hand it off. Encoding, EXIF and
  // upload run in lib/captureQueue so the next shot is never blocked.
  const capture = () => {
    const act = activeRef.current;
    if (!act || !ready) return;
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      toast.error("Camera not ready");
      return;
    }
    setFlash(true);
    vibrate(15);
    setTimeout(() => setFlash(false), 150);

    try {
      const MAX_EDGE = 2400; // long-edge cap keeps uploads efficient
      const srcW = video.videoWidth;
      const srcH = video.videoHeight;
      const scale = Math.min(1, MAX_EDGE / Math.max(srcW, srcH));
      const outW = Math.round(srcW * scale);
      const outH = Math.round(srcH * scale);
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      canvas.getContext("2d").drawImage(video, 0, 0, outW, outH);

      const shotSeq = seqRef.current;
      seqRef.current = shotSeq + 1;
      const tags = shotTags(act.base_tags, skuRef.current);
      const filename = buildFilename(act.name, tags, shotSeq);
      const nextActive = { ...act, current_sequence: shotSeq + 1, photo_count: (act.photo_count || 0) + 1 };
      activeRef.current = nextActive;
      setSeq(shotSeq + 1);
      setActive(nextActive);
      LocalStore.setActiveSession(nextActive);
      setShotCount((n) => n + 1);

      queueShot({
        canvas, sessionId: act.id, filename, sequence: shotSeq, tags, width: outW, height: outH,
        exif: { sessionName: act.name, tags, sequence: shotSeq, filename },
        metadata: { device: navigator.userAgent, width: outW, height: outH, captured_at: new Date().toISOString(),
          ...(skuRef.current ? { sku: skuRef.current } : {}) },
        onEncoded: (dataUrl) =>
          setThumbs((prev) => [{ filename, dataUrl, seq: shotSeq }, ...prev].sort((a, b) => b.seq - a.seq).slice(0, 12)),
        onUploaded: (done) => done?.photo_id && setLastPhoto({ id: done.photo_id, filename, seq: shotSeq }),
        onQueuedOffline: (offline) =>
          toast.message(offline ? "Saved offline — will sync when online" : `Upload retry queued — ${filename}`, { duration: 2200 }),
        onError: () => toast.error(`Could not save ${filename}`),
      });
    } catch (e) {
      console.error(e);
      vibrate([60, 40, 60]);
      toast.error("Capture failed — retry");
    }
  };

  // Burst: N frames at the configured spacing (capture settings).
  const burst = () => {
    if (burstTimer.current) return;
    const { burstCount, burstGapMs } = settingsRef.current;
    setBursting(true);
    let n = 0;
    const fire = () => {
      capture();
      n += 1;
      if (n < burstCount) burstTimer.current = setTimeout(fire, burstGapMs);
      else { burstTimer.current = null; setBursting(false); }
    };
    fire();
  };

  // Barcode → sticky SKU tag for every next photo, then fire a burst.
  const onScan = (raw) => {
    const code = cleanSku(raw);
    if (!code) return;
    // A new scan supersedes any burst in flight so one burst never mixes SKUs.
    clearTimeout(burstTimer.current);
    burstTimer.current = null;
    setSku(code);
    vibrate([20, 30, 20]);
    toast.success(`SKU ${code}`, { duration: 1500 });
    burst();
  };

  const stopTimelapse = () => {
    clearInterval(tlTimer.current);
    tlTimer.current = null;
    setTl((t) => ({ ...t, running: false }));
  };
  const startTimelapse = () => {
    const { tlIntervalS, tlShots } = settingsRef.current;
    let taken = 0;
    const tick = () => {
      captureRef.current();
      taken += 1;
      setTl({ running: true, taken });
      if (tlShots && taken >= tlShots) {
        stopTimelapse();
        toast.success(`Timelapse done · ${taken} shots`);
      }
    };
    setTl({ running: true, taken: 0 });
    tick();
    if (!tlShots || tlShots > 1) tlTimer.current = setInterval(tick, tlIntervalS * 1000);
  };
  const toggleTimelapse = () => (tlTimer.current || tl.running ? stopTimelapse() : startTimelapse());
  useEffect(() => () => clearInterval(tlTimer.current), []);

  useBarcodeScan(videoRef, scanOn && ready, (raw) => onScanRef.current(raw));

  // Tap = single shot, press-and-hold (≥450 ms) = burst.
  const onShutterDown = () => {
    holdTimer.current = setTimeout(() => { holdTimer.current = "fired"; burst(); }, 450);
  };
  const onShutterUp = () => {
    if (holdTimer.current === "fired") { holdTimer.current = null; return; }
    clearTimeout(holdTimer.current);
    holdTimer.current = null;
    capture();
  };
  const onShutterCancel = () => {
    if (holdTimer.current !== "fired") clearTimeout(holdTimer.current);
    holdTimer.current = null;
  };

  // Keyboard / foot pedal / barcode scanner (see lib/keyScanner).
  useEffect(() => {
    const onKey = createKeyRouter({
      onShot: () => captureRef.current(),
      onBurst: () => burstRef.current(),
      onScan: (code) => onScanRef.current(code),
    });
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => onPendingChange(setPendingUploads), []);
  useEffect(() => () => clearTimeout(burstTimer.current), []);
  useEffect(() => {
    const warn = (e) => { if (pendingCount() > 0) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);
  captureRef.current = capture;
  burstRef.current = burst;
  onScanRef.current = onScan;

  const exitToGallery = () => {
    stopCamera();
    if (shotCount > 0) CaptureMoment.recordBatch(shotCount, active?.name);
    nav("/gallery");
  };

  const close = () => {
    stopCamera();
    if (shotCount > 0) CaptureMoment.recordBatch(shotCount, active?.name);
    nav("/sessions");
  };

  // iter-23: hold-or-tap voice note. Records up to 8s, transcribes via Whisper,
  // and appends comma-split words as `display_tags` to the most recent photo.
  // No-op (with a hint) if no photo has been captured yet in this session view.
  const recordVoiceNote = async () => {
    if (voiceNoteListening) {
      if (voiceStopRef.current) voiceStopRef.current();
      return;
    }
    if (!RecorderSupported) {
      toast.error("VOICE NOTES NOT SUPPORTED ON THIS BROWSER");
      return;
    }
    if (!lastPhoto) {
      toast("CAPTURE A PHOTO FIRST — VOICE NOTE ATTACHES TO MOST RECENT SHOT");
      return;
    }
    setVoiceNoteListening(true);
    const handle = recordAndTranscribe({
      maxMs: 8000,
      prompt: "Photographer's quick note: short keywords or a phrase describing the photo.",
    });
    voiceStopRef.current = handle.stop;
    try {
      const text = await handle.promise;
      if (!text) {
        toast("NO SPEECH DETECTED");
        return;
      }
      const tags = parseTagList(text);
      const display_tags = tags.length > 0 ? tags : [text.slice(0, 64)];
      await Photos.patchDisplay(lastPhoto.id, { display_tags });
      toast.success(`VOICE NOTE → ${lastPhoto.filename}`);
    } catch (err) {
      const msg = String(err?.message || err);
      if (msg.includes("not-allowed") || msg.includes("denied") || msg.includes("NotAllowed")) {
        toast.error("MIC PERMISSION DENIED");
      } else if (msg.includes("503")) {
        toast.error("WHISPER UNAVAILABLE · CHECK ADMIN > BILLING");
      } else {
        toast.error(`VOICE NOTE: ${msg.toUpperCase().slice(0, 80)}`);
      }
    } finally {
      voiceStopRef.current = null;
      setVoiceNoteListening(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col" data-testid="camera-page">
      {/* Top status bar */}
      <div className="absolute top-0 inset-x-0 z-20 safe-top">
        <div className="flex items-center justify-between px-4 py-3 bg-black/85 backdrop-blur-sm border-b-2 border-white/20">
          <button
            onClick={close}
            className="w-11 h-11 bg-white text-black flex items-center justify-center border-2 border-white active:scale-[0.95]"
            data-testid="camera-close-btn"
            aria-label="Close camera"
          >
            <X size={20} weight="bold" />
          </button>
          <div className="text-center px-2 truncate flex-1">
            <div className="flex items-center justify-center gap-1.5 mb-0.5">
              {ready ? (
                <CheckCircle size={11} weight="fill" className="text-[#FF2A2A]" />
              ) : (
                <Circle size={11} weight="bold" className="opacity-50" />
              )}
              <p className="label-mono opacity-70 text-[10px]">
                {ready ? "READY" : "STANDBY"}
              </p>
            </div>
            <p className="font-display font-bold truncate max-w-[55vw] mx-auto">
              {active?.name}
            </p>
          </div>
          <div className="font-mono text-right min-w-[80px]">
            <p className="label-mono opacity-70 text-[10px]">NEXT</p>
            <p className="font-black text-3xl leading-none tracking-tight" data-testid="camera-seq-counter">
              {padSeq(seq)}
            </p>
          </div>
        </div>
        <div className="px-4 py-2 bg-black/60 border-b border-white/10 flex items-center justify-between gap-3">
          <p className="font-mono text-xs truncate flex-1">
            <span className="opacity-60">FILE → </span>
            <span data-testid="camera-filename-preview">
              {buildFilename(active?.name, shotTags(active?.base_tags, sku), seq)}
            </span>
          </p>
          {/* iter-37: device picker for multi-camera setups (external
              webcam + built-in, multi-lens phones with wide/tele/macro).
              Only shows once 2+ cameras have been enumerated (which
              happens after the first successful getUserMedia grant). */}
          {devices.length > 1 && (
            <select
              value={deviceId || ""}
              onChange={(e) => pickDevice(e.target.value)}
              className="bg-black border-2 border-white text-white text-[10px] font-mono px-2 py-1 max-w-[140px]"
              data-testid="camera-device-picker"
              aria-label="Choose camera"
            >
              {devices.map((d, i) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || `CAM ${i + 1}`}
                </option>
              ))}
            </select>
          )}
          {RecorderSupported && (
            <button
              type="button"
              onClick={recordVoiceNote}
              data-testid={voiceNoteListening ? "camera-voice-note-listening" : "camera-voice-note-btn"}
              aria-label="Voice note for last photo"
              className={`w-9 h-9 border-2 flex items-center justify-center transition-colors disabled:opacity-40 ${
                voiceNoteListening
                  ? "bg-red-600 text-white border-red-600 animate-pulse"
                  : lastPhoto
                  ? "bg-white text-black border-white"
                  : "bg-transparent text-white/60 border-white/40"
              }`}
              title={lastPhoto ? `Voice note for ${lastPhoto.filename}` : "Capture a photo first"}
            >
              {RecorderSupported ? (
                <Microphone size={16} weight="bold" />
              ) : (
                <MicrophoneSlash size={16} weight="bold" />
              )}
            </button>
          )}
          {shotCount > 0 && (
            <span className="chip chip-blue text-[9px] tracking-[0.15em]" data-testid="session-shot-count">
              {shotCount} CAPTURED
            </span>
          )}
        </div>
      </div>

      {/* Viewport */}
      <div className="flex-1 relative overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="absolute inset-0 w-full h-full object-cover bg-neutral-950"
          data-testid="camera-video"
        />
        {flash && (
          <div className="absolute inset-0 bg-white shutter-flash pointer-events-none z-10" />
        )}
        {errorMsg && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-6 bg-black/80" data-testid={`camera-error-${errorKind || "generic"}`}>
            <div className="bg-white text-black border-2 border-white p-6 max-w-md text-center">
              <p className="font-display font-bold text-xl mb-2">CAMERA UNAVAILABLE</p>
              <p className="text-sm mb-4">{errorMsg}</p>
              {errorKind === "in-use" && (
                <p className="label-mono text-[10px] text-neutral-500 mb-3">
                  MOST COMMON ON DESKTOP · CLOSE ZOOM/MEET/OBS FIRST
                </p>
              )}
              {errorKind === "permission" && (
                <p className="label-mono text-[10px] text-neutral-500 mb-3">
                  ANDROID CHROME → SITE SETTINGS · IOS SAFARI → SETTINGS › SAFARI › CAMERA
                </p>
              )}
              <button
                onClick={() => startCamera(facing, deviceId)}
                className="btn-flat w-full"
                data-testid="camera-retry-btn"
              >
                Retry
              </button>
              <a
                href="/help/camera"
                onClick={(e) => { e.preventDefault(); nav("/help/camera"); }}
                className="label-mono text-[10px] text-neutral-500 hover:text-black underline block mt-3"
                data-testid="camera-error-help-link"
              >
                READ THE 8 COMMON PROBLEMS →
              </a>
            </div>
          </div>
        )}

        {/* Reticle / framing guides */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 inset-x-12 border-t border-white/30" />
          <div className="absolute top-2/3 inset-x-12 border-t border-white/30" />
          <div className="absolute left-1/3 inset-y-24 border-l border-white/30" />
          <div className="absolute left-2/3 inset-y-24 border-l border-white/30" />
        </div>
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-0 inset-x-0 z-20 safe-bottom bg-black/85 border-t-2 border-white/20">
        <CaptureModesBar
          sku={sku}
          onClearSku={() => setSku(null)}
          scanOn={scanOn}
          onToggleScan={() => setScanOn((v) => !v)}
          tl={tl}
          onToggleTl={toggleTimelapse}
          settings={settings}
          onOpenSettings={() => setShowSettings(true)}
          disabled={!!errorMsg || !ready}
        />
        {/* Thumbnail strip */}
        {thumbs.length > 0 && (
          <div className="px-3 pt-3 overflow-x-auto no-scrollbar" data-testid="camera-thumb-strip">
            <div className="flex gap-2">
              {thumbs.map((t, idx) => (
                <button
                  key={t.filename}
                  onClick={() => setPreviewThumb(t)}
                  className={`relative w-16 h-16 border-2 border-white shrink-0 active:scale-95 transition-transform ${idx === 0 ? "slide-in" : ""}`}
                  title={t.filename}
                  data-testid={`camera-thumb-${t.seq}`}
                >
                  <img src={t.dataUrl} alt="" className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 left-0 right-0 bg-black/85 text-[10px] font-mono px-1 text-center truncate font-bold">
                    #{padSeq(t.seq)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 items-center px-6 py-5 gap-4">
          <div className="flex gap-2">
            <button
              onClick={toggleTorch}
              className={`w-12 h-12 border-2 border-white flex items-center justify-center active:scale-[0.95] transition-colors ${torch ? "bg-white text-black" : "bg-black text-white"}`}
              data-testid="camera-torch-btn"
              aria-label="Toggle torch"
            >
              {torch ? <Lightning size={22} weight="fill" /> : <LightningSlash size={22} weight="bold" />}
            </button>
            <button
              onClick={exitToGallery}
              className="relative w-12 h-12 border-2 border-white bg-black flex items-center justify-center active:scale-[0.95]"
              data-testid="camera-gallery-btn"
              aria-label="Open gallery"
            >
              <ImageIcon size={22} weight="bold" />
              {pendingUploads > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1 bg-[#FF2A2A] text-white border-2 border-white font-mono text-[10px] font-bold flex items-center justify-center"
                  data-testid="camera-upload-pending" aria-label={`${pendingUploads} uploading`}>
                  ↑{pendingUploads}
                </span>
              )}
            </button>
          </div>

          <div className="flex justify-center items-center gap-3">
            <button
              onPointerDown={onShutterDown}
              onPointerUp={onShutterUp}
              onPointerLeave={onShutterCancel}
              onPointerCancel={onShutterCancel}
              onContextMenu={(e) => e.preventDefault()}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && e.stopPropagation()}
              onClick={(e) => { if (e.detail === 0) capture(); }}
              disabled={!!errorMsg || !ready}
              className="w-24 h-24 border-4 border-white flex items-center justify-center active:scale-[0.92] disabled:opacity-40 transition-transform touch-none select-none"
              style={{ backgroundColor: "#FF2A2A", boxShadow: "0 0 0 4px #0A0A0A inset" }}
              data-testid="shutter-btn"
              aria-label="Capture (hold for burst)"
            >
              <span className={`w-16 h-16 bg-white transition-transform ${bursting ? "scale-75" : "scale-100"}`} />
            </button>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={burst}
              disabled={!!errorMsg || !ready || bursting}
              className={`h-12 px-2 border-2 border-white flex items-center justify-center font-mono text-[11px] font-bold active:scale-[0.95] disabled:opacity-40 ${bursting ? "bg-white text-black" : "bg-black text-white"}`}
              data-testid="camera-burst-btn"
              aria-label={`Burst ${settings.burstCount} shots`}
              title={`Burst: ${settings.burstCount} shots every ${settings.burstGapMs / 1000}s (hold shutter, or press B)`}
            >
              BURST×{settings.burstCount}
            </button>
            <button
              onClick={flip}
              className="w-12 h-12 border-2 border-white bg-black flex items-center justify-center active:scale-[0.95]"
              data-testid="camera-flip-btn"
              aria-label="Flip camera"
            >
              <ArrowsClockwise size={22} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* Thumbnail tap preview overlay */}
      {previewThumb && (
        <div
          className="fixed inset-0 z-30 bg-black/95 flex flex-col modal-in"
          onClick={() => setPreviewThumb(null)}
          data-testid="camera-thumb-preview"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b-2 border-white/20 safe-top">
            <p className="font-mono text-xs truncate">{previewThumb.filename}</p>
            <button
              onClick={() => setPreviewThumb(null)}
              className="w-10 h-10 bg-white text-black flex items-center justify-center border-2 border-white"
              data-testid="camera-thumb-preview-close"
              aria-label="Close preview"
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4">
            <img src={previewThumb.dataUrl} alt="" className="max-w-full max-h-full object-contain border-2 border-white" />
          </div>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
      {showSettings && (
        <CaptureSettingsSheet settings={settings} onChange={setSettings} onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}
